import { addTypedProvider } from '../../metaData.js';
import { MetadataModules } from '../../enums/index.js';
import { calculateSUVScalingFactors } from '@cornerstonejs/calculate-suv';
function timeToString(v) {
    if (typeof v === 'string')
        return v;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
        const t = v;
        const hours = `${t?.hours ?? '00'}`.padStart(2, '0');
        const minutes = `${t?.minutes ?? '00'}`.padStart(2, '0');
        const seconds = `${t?.seconds ?? '00'}`.padStart(2, '0');
        const fractionalSeconds = `${t?.fractionalSeconds ?? '000000'}`.padEnd(6, '0');
        return `${hours}${minutes}${seconds}.${fractionalSeconds}`;
    }
    return v;
}
function dateToString(v) {
    if (typeof v === 'string')
        return v;
    if (v && typeof v === 'object' && !Array.isArray(v) && 'year' in v) {
        const d = v;
        const month = `${d.month}`.padStart(2, '0');
        const day = `${d.day}`.padStart(2, '0');
        return `${d.year}${month}${day}`;
    }
    return v;
}
function parseNumber(v) {
    if (typeof v === 'number' && !Number.isNaN(v))
        return v;
    if (typeof v === 'string') {
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
}
function getRadiopharmaceuticalInfo(data) {
    const ri = data.RadiopharmaceuticalInfo;
    if (ri == null || typeof ri !== 'object')
        return undefined;
    const first = Array.isArray(ri)
        ? ri[0]
        : (ri[0] ?? ri);
    return first && typeof first === 'object' && !Array.isArray(first)
        ? first
        : undefined;
}
function buildPTInstanceMetadataFromData(data) {
    if (data.Modality !== 'PT')
        return null;
    const { SeriesDate: seriesDate, SeriesTime: seriesTime, AcquisitionDate: acquisitionDate, AcquisitionTime: acquisitionTime, PatientWeight: patientWeight, CorrectedImage: correctedImage, Units: units, DecayCorrection: decayCorrection, } = data;
    const ri = getRadiopharmaceuticalInfo(data);
    if (!ri)
        return null;
    const radionuclideTotalDose = parseNumber(ri.RadionuclideTotalDose);
    const radionuclideHalfLife = parseNumber(ri.RadionuclideHalfLife);
    const radiopharmaceuticalStartDateTime = ri.RadiopharmaceuticalStartDateTime;
    const radiopharmaceuticalStartTime = ri.RadiopharmaceuticalStartTime;
    const patientWeightNum = parseNumber(patientWeight);
    if (seriesDate === undefined ||
        seriesTime === undefined ||
        patientWeightNum === undefined ||
        acquisitionDate === undefined ||
        acquisitionTime === undefined ||
        correctedImage === undefined ||
        units === undefined ||
        decayCorrection === undefined ||
        radionuclideTotalDose === undefined ||
        radionuclideHalfLife === undefined) {
        return null;
    }
    const toDate = (v) => typeof v === 'string'
        ? v
        : v && typeof v === 'object' && 'year' in v
            ? dateToString(v)
            : v;
    const toTime = (v) => typeof v === 'string'
        ? v
        : v && typeof v === 'object'
            ? timeToString(v)
            : v;
    const correctedImageValue = typeof correctedImage === 'string'
        ? correctedImage.split('\\')
        : Array.isArray(correctedImage)
            ? correctedImage
            : correctedImage;
    const instanceMetadata = {
        CorrectedImage: correctedImageValue,
        Units: units,
        RadionuclideHalfLife: radionuclideHalfLife,
        RadionuclideTotalDose: radionuclideTotalDose,
        DecayCorrection: decayCorrection,
        PatientWeight: patientWeightNum,
        SeriesDate: dateToString(seriesDate),
        SeriesTime: timeToString(seriesTime),
        AcquisitionDate: dateToString(acquisitionDate),
        AcquisitionTime: timeToString(acquisitionTime),
    };
    if (radiopharmaceuticalStartDateTime !== undefined) {
        instanceMetadata.RadiopharmaceuticalStartDateTime = dateToString(radiopharmaceuticalStartDateTime);
    }
    if (radiopharmaceuticalStartTime !== undefined) {
        instanceMetadata.RadiopharmaceuticalStartTime = timeToString(radiopharmaceuticalStartTime);
    }
    if (data.PatientSex !== undefined)
        instanceMetadata.PatientSex = data.PatientSex;
    if (data.PatientSize !== undefined)
        instanceMetadata.PatientSize = data.PatientSize;
    return instanceMetadata;
}
function scalingFromInstanceProvider(next, query, data, options) {
    if (data == null || typeof data !== 'object') {
        return next(query, data, options);
    }
    const d = data;
    if (d.Modality === 'RTDOSE') {
        const doseGridScaling = parseNumber(d.DoseGridScaling);
        const { DoseSummation, DoseType, DoseUnit } = d;
        if (doseGridScaling !== undefined ||
            DoseSummation !== undefined ||
            DoseType !== undefined ||
            DoseUnit !== undefined) {
            return {
                DoseGridScaling: doseGridScaling,
                DoseSummation,
                DoseType,
                DoseUnit,
            };
        }
    }
    if (d.Modality === 'PT') {
        const instanceMetadata = buildPTInstanceMetadataFromData(d);
        if (!instanceMetadata) {
            return next(query, undefined, options);
        }
        try {
            const factors = calculateSUVScalingFactors([instanceMetadata]);
            return factors[0] ?? next(query, undefined, options);
        }
        catch {
            return next(query, undefined, options);
        }
    }
    return next(query, undefined, options);
}
export function registerScalingFromInstanceProvider() {
    addTypedProvider(MetadataModules.SCALING, scalingFromInstanceProvider, { priority: 0, isDefault: true });
}
